import {
  AutoSave,
  Button,
  Card,
  closeCurrentDialog,
  DataStatus,
  Form,
  Label,
  openDialog,
  Separator,
} from 'platform/components';
import {Box, Heading, HStack, Show, VStack} from 'platform/foundation';

import {useEffect, useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';
import {useDispatch, useStore} from 'react-redux';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  CreateMinipurchaseApiArg,
  interestApi,
  MinipurchaseResponseBody,
  UpdateMinipurchaseApiArg,
  useCreateMinipurchaseMutation,
  useUpdateMinipurchaseMutation,
} from '@dms/api/finance';
import {useGetAiLeadEmailMessageQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {interestRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {
  type DmsDataGridProps,
  handleApiError,
  queryParams,
  Section,
  useInterestEditPermissions,
  EmailLeadPreviewDialog,
  MessageText,
  useTenant,
} from '@dms/shared';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  getSafely,
  handleFormReset,
  isNotNilOrEmpty,
  useNavigate,
} from 'shared/web';

import {VehicleRecommendations} from '../../components/VehicleRecommendations/VehicleRecommendations';
import {useInterestData} from '../../hooks/useInterestData';
import {ExternalFilterConnectorRef} from './components/ExternalFilterConnector';
import {ExternalFilterForm} from './components/ExternalFilterForm';
import {OfferableVehiclesDatagrid} from './components/SellingV2/components/OfferableVehiclesDatagrid';
import {OfferedVehicles} from './components/SellingV2/components/OfferedVehicles';
import {PaymentForm} from './components/SellingV2/components/PaymentForm';
import {TestDriveForm} from './components/SellingV2/components/TestDriveForm';
import {ExternalFilter} from './types/ExternalFilter';
import {buildMinipurchaseUpdateBody} from './utils/buildMinipurchaseUpdateBody';
import {buildTestDriveSlot} from './utils/buildTestDriveSlot';
import {EXTERNAL_FILTER_ID} from './utils/externalFilterId';
import {transformExternalFilterToMinipurchaseSpecification} from './utils/transformExternalFilterToMinipurchaseSpecification';
import {transformMinipurchaseToExternalFilter} from './utils/transformMinipurchaseToExternalFilter';

interface SellingV2Props extends Pick<DmsDataGridProps, 'maximization'> {
  interestId: string;
}

export function SellingV2(props: SellingV2Props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {interest, miniPurchase, isLoading, isError, offeredVehiclesList} = useInterestData(
    props.interestId
  );

  const {data: aiLeadEmailMessage} = useGetAiLeadEmailMessageQuery(
    {id: interest?.originLeadMessageId ?? ''},
    {
      skip: isNilOrEmpty(interest?.originLeadMessageId),
    }
  );

  const store = useStore();

  // fixedCacheKey stores mutation state in the Redux store under a stable key,
  // allowing us to check if a mutation is in-flight via store.getState() — immune to stale closures
  // caused by AutoSave's debounced callbacks firing from old render cycles.
  const createMinipurchaseCacheKey = `create-minipurchase-${props.interestId}`;
  const [createMinipurchase] = useCreateMinipurchaseMutation({
    fixedCacheKey: createMinipurchaseCacheKey,
  });
  const updateMinipurchaseCacheKey = `update-minipurchase-${props.interestId}`;
  const [updateMinipurchase] = useUpdateMinipurchaseMutation({
    fixedCacheKey: updateMinipurchaseCacheKey,
  });

  const externalFilterRef = useRef<ExternalFilterConnectorRef>(null);
  const pendingMinipurchaseValuesRef = useRef<ExternalFilter | null>(null);
  // Minipurchase is null until the interest + minipurchase refetch completes, which is problematic when the user navigates away from the page
  const latestMinipurchaseRef = useRef<MinipurchaseResponseBody | null>(null);

  const {hasChangeMinisalePriceOfferedInterestPermission} = useInterestEditPermissions({
    interestId: props.interestId,
  });

  const {tenantCurrency} = useTenant();

  // Reads create mutation status directly from the Redux store at call time.
  // We cannot use useRef or closure-captured state because AutoSave's useDebouncedCallback
  // fires callbacks from stale closures (old render cycles), making refs unreliable.
  // Also checks 'fulfilled' status to guard the window between POST success and miniPurchase
  // query refetch completing — during which miniPurchase is still null.
  const isMinipurchaseCreateInProgress = () => {
    const state = store.getState() as {
      rtk_omneticApi?: {mutations?: Record<string, {status?: string}>};
    };
    const mutations = state.rtk_omneticApi?.mutations;
    const mutationState = getSafely(mutations, createMinipurchaseCacheKey);
    return mutationState?.status === 'pending' || mutationState?.status === 'fulfilled';
  };

  // Reads update mutation status directly from the Redux store at call time.
  // Prevents concurrent PUT requests that would cause data loss — when a PUT is in-flight,
  // new values are queued in pendingMinipurchaseValuesRef and flushed after the PUT completes
  // using the server response as the base (avoiding stale miniPurchase from closures).
  const isMinipurchaseUpdateInProgress = () => {
    const state = store.getState() as {
      rtk_omneticApi?: {mutations?: Record<string, {status?: string}>};
    };
    const mutations = state.rtk_omneticApi?.mutations;
    const mutationState = getSafely(mutations, updateMinipurchaseCacheKey);
    return mutationState?.status === 'pending';
  };

  // The grid shows the saved specification from the response. The payment and test-drive forms send
  // only their own fields, so their values would clear the specification from the grid.
  const pushSavedSpecificationToGrid = (savedMinipurchase: MinipurchaseResponseBody) =>
    externalFilterRef.current?.onChange(transformMinipurchaseToExternalFilter(savedMinipurchase));

  // Recursively flushes values queued in pendingMinipurchaseValuesRef after each PUT completes.
  // Called after every successful update to drain any values that accumulated while the PUT was in-flight.
  const flushPendingValues = (base: MinipurchaseResponseBody) => {
    if (!pendingMinipurchaseValuesRef.current) {
      return;
    }

    const pendingValues = pendingMinipurchaseValuesRef.current;
    pendingMinipurchaseValuesRef.current = null;

    const nextArg: UpdateMinipurchaseApiArg = {
      interestId: props.interestId,
      body: buildMinipurchaseUpdateBody(base, pendingValues, tenantCurrency),
    };

    updateMinipurchase(nextArg)
      .unwrap()
      .then((response) => {
        latestMinipurchaseRef.current = response;
        pushSavedSpecificationToGrid(response);
        flushPendingValues(response);
      })
      .catch(handleApiError);
  };

  const onMiniPurchaseUpdate = (values: ExternalFilter) => {
    const baseMinipurchase = miniPurchase ?? latestMinipurchaseRef.current;

    if (!baseMinipurchase || !baseMinipurchase?.id) {
      if (isMinipurchaseCreateInProgress()) {
        pendingMinipurchaseValuesRef.current = {...pendingMinipurchaseValuesRef.current, ...values};
        return;
      }

      const arg: CreateMinipurchaseApiArg = {
        body: {
          interestId: props.interestId,
          filteredSpecifications: transformExternalFilterToMinipurchaseSpecification(values),
          additionalInformation: values.additionalInformation ?? null,
          testDriveRequested: values.testDriveRequested?.[0] === 'yes',
          testDriveScheduledAt: buildTestDriveSlot(
            values.testDriveScheduledAtDate,
            values.testDriveScheduledAtTime
          ),
          testDriveNote: values.testDriveNote,
          preferredDownPayment: values.preferredDownPayment
            ? {
                amount: String(values.preferredDownPayment),
                currency: tenantCurrency,
              }
            : null,
          paymentPreferenceCodeId: values.paymentPreferenceCodeId?.[0] ?? null,
        },
      };
      createMinipurchase(arg)
        .unwrap()
        .then((response) => {
          latestMinipurchaseRef.current = response;
          dispatch(interestApi.util.invalidateTags([{type: 'Interest', id: props.interestId}]));
          pushSavedSpecificationToGrid(response);
          flushPendingValues(response);
        })
        .catch(handleApiError);
    } else {
      if (isMinipurchaseUpdateInProgress()) {
        pendingMinipurchaseValuesRef.current = {...pendingMinipurchaseValuesRef.current, ...values};
        return;
      }

      const arg: UpdateMinipurchaseApiArg = {
        interestId: props.interestId,
        body: buildMinipurchaseUpdateBody(baseMinipurchase, values, tenantCurrency),
      };
      updateMinipurchase(arg)
        .unwrap()
        .then((response) => {
          latestMinipurchaseRef.current = response;
          pushSavedSpecificationToGrid(response);
          flushPendingValues(response);
        })
        .catch(handleApiError);
    }
  };

  // Flush pending values after miniPurchase becomes available (POST completed + interest refetched)
  useEffect(
    function flushPendingMiniPurchaseValues() {
      if (!miniPurchase?.id) {
        return;
      }

      if (pendingMinipurchaseValuesRef.current) {
        const pendingValues = pendingMinipurchaseValuesRef.current;
        pendingMinipurchaseValuesRef.current = null;
        onMiniPurchaseUpdate(pendingValues);
      }
      // onMiniPurchaseUpdate intentionally omitted: including it would cause an infinite loop since
      // it depends on miniPurchase, which changes when this effect flushes pending values
    },
    // eslint-disable-next-line react/exhaustive-deps
    [miniPurchase?.id]
  );

  const handleOpenEmailLeadPreviewDialog = () => {
    openDialog(<EmailLeadPreviewDialog message={aiLeadEmailMessage!} />, {
      id: 'emailLeadPreviewDialog',
      size: 'xLarge',
      title: i18n.t('entity.interest.aiLead.sentEmail'),
      buttons: [
        {
          title: i18n.t('general.actions.close'),
          variant: 'secondary',
          onClick: closeCurrentDialog,
        },
      ],
    });
  };

  const resetRequestSpecification = (formApi: UseFormReturn<ExternalFilter>) => {
    handleFormReset<ExternalFilter>(formApi, [
      {name: 'type', value: ['VEHICLETYPE_PASSENGER_CAR']},
      {name: 'make', value: null},
      {name: 'modelFamily', value: null},
      {name: 'trimLevel', value: null},
      {name: 'fuelType', value: null},
      {name: 'transmission', value: null},
      {name: 'bodyStyle', value: null},
      {name: 'drive', value: null},
      {name: 'features', value: null},
      {name: 'vatDeductible', value: null},
      {name: 'bodyColor', value: null},
      {name: 'labelIds', value: null},
      {name: 'salePresetId', value: null},
      {name: 'cubicCapacityFrom', value: null},
      {name: 'cubicCapacityTo', value: null},
      {name: 'priceFrom', value: null},
      {name: 'priceTo', value: null},
      {name: 'yearFrom', value: null},
      {name: 'yearTo', value: null},
      {name: 'mileageFrom', value: null},
      {name: 'mileageTo', value: null},
      {name: 'powerFrom', value: null},
      {name: 'powerTo', value: null},
      {name: 'condition', value: null},
      {name: 'additionalInformation', value: null},
    ]);
    onMiniPurchaseUpdate(formApi.getValues());
  };

  const navigateToCreateOfferedVehicle = (minipurchaseId: string) =>
    navigate(
      composePath(vehiclesRoutes.createV2, {
        queryParams: {
          [queryParams.REDIRECT_SOURCE]: EntityResourceIds.interest,
          [queryParams.RETURN_URL]: composePath(interestRoutes.detailSelling, {
            params: {id: props.interestId},
          }),
          id: minipurchaseId,
        },
      })
    );

  const handleCreateOfferedVehicle = () => {
    const existingMinipurchaseId = miniPurchase?.id;

    // The created vehicle is attached as an offered vehicle of a minipurchase
    // so a minipurchase must exist first.
    if (isNotNilOrEmpty(existingMinipurchaseId)) {
      navigateToCreateOfferedVehicle(existingMinipurchaseId);
      return;
    }

    createMinipurchase({body: {interestId: props.interestId}})
      .unwrap()
      .then((response) => navigateToCreateOfferedVehicle(response.id))
      .catch(handleApiError);
  };

  const isInterestClosedAnyStatus =
    interest?.state === 'CLOSED' || interest?.state === 'UNSUCCESSFUL';

  return (
    <Section>
      <DataStatus isLoading={isLoading} isError={isError} minHeight={175}>
        <Show when={isNotNilOrEmpty(interest)}>
          <VStack spacing={4}>
            <Card title={i18n.t('entity.interest.aiLead.customerInquiry')}>
              <VStack spacing={6}>
                <Show when={isNotNilOrEmpty(interest?.originLeadMessageId)}>
                  <VStack spacing={1}>
                    <HStack width="100%" justify="space-between" align="flex-end">
                      <Label>{i18n.t('entity.interest.aiLead.originalInquiry')}</Label>
                      <Button
                        size="small"
                        variant="link"
                        title={i18n.t('entity.interest.aiLead.showOriginal')}
                        onClick={() => handleOpenEmailLeadPreviewDialog()}
                        isDisabled={isNilOrEmpty(aiLeadEmailMessage)}
                      />
                    </HStack>
                    <Box
                      padding={4}
                      border="1px solid"
                      borderColor="palettes.neutral.40.100"
                      backgroundColor="palettes.neutral.10.100"
                      borderRadius="medium"
                    >
                      <MessageText htmlData={aiLeadEmailMessage?.body ?? EMPTY_PLACEHOLDER} />
                    </Box>
                  </VStack>
                </Show>

                <Show when={isNotNilOrEmpty(offeredVehiclesList)}>
                  <OfferedVehicles interestId={props.interestId} hideScheduleButton />
                </Show>

                <TestDriveForm
                  interestId={props.interestId}
                  onMinipurchaseUpdate={onMiniPurchaseUpdate}
                  hideScheduleButton
                  isDisabled={isInterestClosedAnyStatus}
                />

                <Separator spacing={0} />

                <PaymentForm
                  interestId={props.interestId}
                  onMinipurchaseUpdate={onMiniPurchaseUpdate}
                  isDisabled={isInterestClosedAnyStatus}
                />

                <Separator spacing={0} />

                <Form<ExternalFilter>
                  data-testid={testIds.interest.detail('requestSpecification')}
                  defaultValues={transformMinipurchaseToExternalFilter(miniPurchase)}
                  isDisabled={
                    !hasChangeMinisalePriceOfferedInterestPermission || isInterestClosedAnyStatus
                  }
                >
                  {(control, formApi) => (
                    <VStack spacing={6}>
                      <HStack width="100%" justify="space-between" align="center">
                        <Heading size={4}>
                          {i18n.t('entity.interest.labels.requestSpecification')}
                        </Heading>
                        <HStack spacing={4} align="center" justify="flex-end" wrap>
                          <Button
                            // size="small"
                            // variant="link"
                            isDisabled={
                              isInterestClosedAnyStatus ||
                              !hasChangeMinisalePriceOfferedInterestPermission
                            }
                            title={i18n.t('entity.vehicle.labels.createVehicle')}
                            // leftIcon="content/add_circle"
                            onClick={handleCreateOfferedVehicle}
                            data-testid={testIds.interest.detail('createOfferedVehicle')}
                          />
                          <Button
                            size="small"
                            variant="link"
                            isDisabled={
                              isInterestClosedAnyStatus ||
                              !hasChangeMinisalePriceOfferedInterestPermission
                            }
                            title={i18n.t('entity.interest.actions.resetSpecification')}
                            leftIcon="action/delete"
                            onClick={() => resetRequestSpecification(formApi)}
                            data-testid={testIds.interest.detail('resetRequestSpecification')}
                          />
                        </HStack>
                      </HStack>
                      <Box minHeight={125}>
                        <AutoSave<ExternalFilter> onSave={onMiniPurchaseUpdate} />
                        <ExternalFilterForm
                          data-testid={testIds.interest.detail('requestSpecification')}
                          control={control}
                          formApi={formApi}
                          onChange={onMiniPurchaseUpdate}
                        />
                        <div id={EXTERNAL_FILTER_ID} />
                      </Box>
                    </VStack>
                  )}
                </Form>
              </VStack>
            </Card>
            <VehicleRecommendations
              interestId={props.interestId}
              data-testid={testIds.interest.detail('vehicleRecommendations')}
            />
            <Box height={175}>
              <OfferableVehiclesDatagrid
                maximization={props.maximization}
                interestId={props.interestId}
                externalFilterRef={externalFilterRef}
              />
            </Box>
          </VStack>
        </Show>
      </DataStatus>
    </Section>
  );
}
